import {
  capitalize
} from 'lodash';
import {
  getControllerCandidateResumeSubmission
} from '../../../../components/Candidates/ResumeSubmission/CandidateResumeSubmission';
import {
  KEY__CREDENTIAL__PRIMARY,
  KEY__CREDENTIAL__SECONDARY
} from '../../../../dictionaries/Credential.dic';
import {
  KEY__PROVIDER__GREENHOUSE,
  KEY__PROVIDER__GREENHOUSE_HARVEST
} from '../../../../dictionaries/Providers.dic';
import {
  Arr
} from '../../../Array.lib';
import Base64 from '../../../base64';
import {
  NOT
} from '../../../Boolean.lib';
import fetchCandidateResume from '../../../Candidate/fetchCandidateResume.async';
import Core from '../../../Core';
import {
  EMPLOYER_SUBMISSION_METHOD__ATS,
  ID__ATS_TYPE__GH_HARVEST
} from '../../../Definition';
import {
  tools
} from '../../../GenericTools.lib';
import {
  joinHTML
} from '../../../HTML.lib';
import Http from '../../../Http';
import {
  Obj
} from '../../../Object.lib';
import {
  sendSafeEmail
} from '../../Email/Email.lib';
import getAtsContextProperty from '../getAtsContextProperty.fun';
import {
  mapGreenhousePayload
} from './mapGreenhousePayload.fun';

export async function submitGreenhouseCandidate({
  candidate = {},
  job = {},
  emailParams = {},
  submissionNotesPublic = '',
  submissionNotesPrivate = ''
} = {}) {
  const CAPITALIZED__PROVIDER = capitalize(KEY__PROVIDER__GREENHOUSE);
  const results = {};
  try {
    const { employerId, employer = {} } = job;
    const { resumes = [] } = candidate;
    const resume = resumes[0];

    const ID__ATS__JOB = getAtsContextProperty({
      entity: job,
      atsTypeId: ID__ATS_TYPE__GH_HARVEST,
      property: 'jobId'
    }).trim();

    if (NOT(ID__ATS__JOB)) {
      Core.showError(
        `10x10 - Missing Greenhouse Job ID in configuration`
      );
      return;
    }

    const ID__ATS__SOURCE = getAtsContextProperty({
      entity: employer,
      atsTypeId: ID__ATS_TYPE__GH_HARVEST,
      property: 'sourceId'
    }).trim();

    const ID__ATS__REFERRER = getAtsContextProperty({
      entity: employer,
      atsTypeId: ID__ATS_TYPE__GH_HARVEST,
      property: 'referrerId'
    }).trim();

    // SUBMIT CANDIDATE TO ATS
    Core.showLoader(
      `${CAPITALIZED__PROVIDER} - Submitting candidate`
    );

    const {
      candidate: payload = {},
      errors = [],
    } = mapGreenhousePayload({
      candidate,
      atsJobId: ID__ATS__JOB,
      atsSourceId: ID__ATS__SOURCE,
      atsReferrerId: ID__ATS__REFERRER
    });
    results.errors = Arr(results.errors);
    results.errors.unshift(...errors);

    const {
      candidate: atsCandidate = {},
      application: atsApplication = {}
    } = Obj(
      await Http.post(
        Core.getApi('Engagements/postAts'),
        {
          PROVIDER: KEY__PROVIDER__GREENHOUSE,
          SUB_GROUP: KEY__PROVIDER__GREENHOUSE_HARVEST,
          ACTION: 'post',
          EMPLOYER_ID: employerId,
          PATH: '/candidates',
          BODY: payload
        }
      )
    );

    const ID__ATS__CANDIDATE = +(atsCandidate.id);
    if (!ID__ATS__CANDIDATE) {
      const error = `Greenhouse - Candidate submission failed`;
      results.errors = Arr(results.errors);
      results.errors.unshift(error);
      // ❌ Escape process if there is an error submitting candidate
      throw error;
    }

    const ID__ATS__APPLICATION = +(atsApplication?.id);
    if (NOT(ID__ATS__APPLICATION)) {
      const error = `Greenhouse - Application creation failed`;
      results.errors = Arr(results.errors);
      results.errors.unshift(error);
      // ❌ Escape process if there is an error creating application
      throw error;
    }

    // UPLOAD RESUME TO ATS
    try {
      if (resume && resume.url && resume.filename) {
        Core.showLoader(
          `${CAPITALIZED__PROVIDER} - Uploading resume`
        );
        const filename = tools.getFilenameFromURL(resume.url);
        if (!!filename) {
          const resumeBlob = await fetchCandidateResume({
            key: 'pdf',
            type: 'blobRaw',
            url: resume.url,
            source: '_runSovrenParser'
          });
          const content = await new Promise((resolve, reject) => {
            let reader = new FileReader();
            reader.onload = async event => {
              try {
                resolve(Base64.encodeArray(event.target.result));
              } catch (ex) {
                reject(ex);
              }
            }
            reader.readAsArrayBuffer(resumeBlob);
          });
          const payload = ({
            application_id: ID__ATS__APPLICATION,
            filename,
            content,
            type: 'resume'
          });
          await Http.post(
            Core.getApi('Engagements/postAts'),
            {
              PROVIDER: KEY__PROVIDER__GREENHOUSE,
              SUB_GROUP: KEY__PROVIDER__GREENHOUSE_HARVEST,
              ACTION: 'post',
              EMPLOYER_ID: employerId,
              PATH: '/attachments',
              BODY: payload
            }
          );
        }

      }
    }
    catch (error) {
      const _error = `Greenhouse - Resume upload failed`;
      results.errors = Arr(results.errors);
      results.errors.unshift(_error);
      console.error(_error, error);
      // ⚠️ Continue process even if there is an error uploading resume
    }

    // CREATE NOTES IN ATS
    try {
      Core.showLoader(
        `${CAPITALIZED__PROVIDER} - Posting submission notes`
      );
      await Http.post(
        Core.getApi('Engagements/postAts'),
        {
          PROVIDER: KEY__PROVIDER__GREENHOUSE,
          SUB_GROUP: KEY__PROVIDER__GREENHOUSE_HARVEST,
          ACTION: 'post',
          EMPLOYER_ID: employerId,
          PATH: '/notes',
          BODY: {
            candidate_id: ID__ATS__CANDIDATE,
            application_id: ID__ATS__APPLICATION,
            body: joinHTML(submissionNotesPublic),
            note_type: 'ACTIVITY',
            visibility: 'public'
          }
        }
      );
      await Http.post(
        Core.getApi('Engagements/postAts'),
        {
          PROVIDER: KEY__PROVIDER__GREENHOUSE,
          SUB_GROUP: KEY__PROVIDER__GREENHOUSE_HARVEST,
          ACTION: 'post',
          EMPLOYER_ID: employerId,
          PATH: '/notes',
          BODY: {
            candidate_id: ID__ATS__CANDIDATE,
            application_id: ID__ATS__APPLICATION,
            body: joinHTML(submissionNotesPrivate),
            note_type: 'ACTIVITY',
            visibility: 'admin_only'
          }
        }
      );
    }
    catch (error) {
      const _error = `Greenhouse - Note submission failed`;
      results.errors = Arr(results.errors);
      results.errors.unshift(_error);
      // ⚠️ Continue process even if there is an error sending notes
    }

    // SEND EMAIL TO ADMIN
    Core.showLoader(
      `Completing submission process`
    );
    const emailOptions = {
      ...emailParams,
      to: Core.getResumeSubmissionBcc()
    };
    emailOptions.subject = (
      `Submitted via ${CAPITALIZED__PROVIDER} API${emailOptions.subject ? ` | ${emailOptions.subject}` : ''}`
    );
    emailOptions.source = 'submitGreenhouseCandidate__sendEmail';
    emailOptions.key = KEY__CREDENTIAL__SECONDARY;
    emailOptions.replyTo = KEY__CREDENTIAL__PRIMARY;
    await sendSafeEmail(emailOptions);

    Core.showSuccess(
      `Submitted candidate successfully!`
    );

    // MAKE STAGE TRANSITION
    await getControllerCandidateResumeSubmission().transitionAfterSubmission({
      submissionMethodId: EMPLOYER_SUBMISSION_METHOD__ATS,
      atsTypeId: employer.atsTypeId,
      atsContextUpdate: {
        applicationId: ID__ATS__APPLICATION,
        candidateId: ID__ATS__CANDIDATE,
        jobId: ID__ATS__JOB
      }
    });

  }
  catch (error) {
    Core.showError('Failed to submit candidate to Greenhouse. Try re-connect your account.', error);
    const emailOptions = {
      ...emailParams,
      to: Core.getResumeSubmissionBcc()
    };
    emailOptions.subject = (
      `ERROR | ${CAPITALIZED__PROVIDER} API failed${emailOptions.subject ? ` | ${emailOptions.subject}` : ''}`
    );
    emailOptions.html = joinHTML(
      `
          --------------------------------------------------
          <br/><br/>
          Error:<br/>
          ${error}
          <br/><br/>
          --------------------------------------------------
          <br/><br/>
          ${emailOptions.html}
        `
    );
    emailOptions.html = emailOptions.html.replace(/\<\/?html\>/gi, '');
    emailOptions.html = emailOptions.html.replace(/\<\/?head\>/gi, '');
    emailOptions.html = emailOptions.html.replace(/\<\/?body\>/gi, '');
    emailOptions.source = 'submitCandidateToGreenhouseHarvest';
    emailOptions.key = KEY__CREDENTIAL__SECONDARY;
    emailOptions.replyTo = KEY__CREDENTIAL__PRIMARY;
    sendSafeEmail(emailOptions);
  }
  return results;
}